// Section 6: Probability & Statistics — pages 138-159
// Grade9Ready GCSE Maths topic bank
// Focus: probability, experimental probability, Venn diagrams, charts, averages,
// grouped data, scatter graphs, tree diagrams and conditional probability.

const PAGE_IMAGES = {
  p138_139:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/d1hvhuyu_Page138-139.png",
  p140_141:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/k8259xwf_Page140-141.png",
  p142_143:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/ozcfis4f_Page142-143.png",
  p144_145:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/jje6zthv_Page144-145.png",
  p146_147:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/h02ktq1b_Page146-147.png",
  p148_149:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/02io4mfm_Page148-149.png",
  p150_151:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/23n4sttj_Page150-151.png",
  p152_153:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/mose7oi1_Page152-153.png",
  p154_155:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/npvmx1oz_Page154-155.png",
  p156_157:
    "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/edy3qufy_Page156-157.png",
};

export const probabilityTopics = [
  {
    id: "6.1",
    title: "Probability Basics",
    slug: "probability-basics",
    pageRef: "Pages 138-141",
    difficulty: "foundation",
    learn: {
      explanation:
        "Probability measures how likely an event is to happen. It is always between 0 and 1. For equally likely outcomes, divide the number of favourable outcomes by the total number of possible outcomes.",
      keyPoints: [
        "0 means impossible; 1 means certain",
        "P(event) = favourable outcomes ÷ total outcomes",
        "All probabilities for all possible outcomes add to 1",
        "P(not A) = 1 − P(A)",
        "Expected frequency = probability × number of trials",
        "A sample space shows all possible outcomes",
      ],
      workedExamples: [
        {
          problem:
            "A bag contains 6 blue balls, 5 red balls and 9 green balls. Find P(green) and P(not green).",
          image: PAGE_IMAGES.p138_139,
          steps: [
            {
              step: 1,
              instruction: "Find the total number of balls.",
              working: "6 + 5 + 9 = 20",
            },
            {
              step: 2,
              instruction: "Find the probability of green.",
              working: "P(green) = 9/20 = 0.45",
            },
            {
              step: 3,
              instruction: "Use the complement for not green.",
              working: "P(not green) = 1 − 0.45 = 0.55",
            },
          ],
          answer: "P(green) = 0.45 and P(not green) = 0.55",
        },
        {
          problem:
            "A spinner has 8 equal sections numbered 1 to 8. Find P(even number).",
          image: PAGE_IMAGES.p138_139,
          steps: [
            {
              step: 1,
              instruction: "List the favourable outcomes.",
              working: "Even numbers: 2, 4, 6, 8",
            },
            {
              step: 2,
              instruction: "Count favourable outcomes and total outcomes.",
              working: "4 favourable outcomes out of 8 total outcomes",
            },
            {
              step: 3,
              instruction: "Write and simplify the probability.",
              working: "P(even) = 4/8 = 1/2 = 0.5",
            },
          ],
          answer: "1/2",
        },
      ],
      whyItWorks:
        "Probability compares the outcomes you want with all possible outcomes, so it gives a consistent numerical measure of chance.",
    },
    practice: {
      warmUp: [
        {
          id: "6.1.w1",
          type: "choice",
          question: "A probability of 1 means the event is:",
          options: ["Impossible", "Unlikely", "Evens", "Certain"],
          answer: "Certain",
          hint1: "1 means the event definitely happens.",
          hint2: "0 = impossible and 1 = certain.",
          hint3: "Certain",
        },
        {
          id: "6.1.w2",
          type: "choice",
          question: "A probability of 0 means the event is:",
          options: ["Impossible", "Likely", "Certain", "Evens"],
          answer: "Impossible",
          hint1: "0 means there is no chance.",
          hint2: "The event cannot happen.",
          hint3: "Impossible",
        },
      ],
      level1: [
        {
          id: "6.1.1",
          type: "number",
          question: "A bag has 8 red balls and 8 blue balls. Find P(red).",
          answer: "0.5",
          hint1: "There are 8 red balls out of 16 total balls.",
          hint2: "8/16",
          hint3: "0.5",
        },
        {
          id: "6.1.2",
          type: "number",
          question: "P(bus being late) = 0.7. Find P(bus NOT being late).",
          answer: "0.3",
          hint1: "Use P(not A) = 1 − P(A).",
          hint2: "1 − 0.7",
          hint3: "0.3",
        },
      ],
      level2: [
        {
          id: "6.1.3",
          type: "number",
          question:
            "P(winning a game) = 0.12. In 50 games, how many wins are expected?",
          answer: "6",
          hint1: "Expected frequency = probability × trials.",
          hint2: "0.12 × 50",
          hint3: "6",
        },
        {
          id: "6.1.4",
          type: "number",
          question:
            "A fair dice is rolled 300 times. How many 5s would you expect?",
          answer: "50",
          hint1: "P(5) = 1/6.",
          hint2: "300 × 1/6",
          hint3: "50",
        },
      ],
      level3: [
        {
          id: "6.1.5",
          type: "number",
          question:
            "A biased spinner has P(red) = 0.35, P(blue) = 0.25 and P(green) = 0.15. Find P(yellow).",
          answer: "0.25",
          hint1: "All probabilities must add to 1.",
          hint2: "0.35 + 0.25 + 0.15 = 0.75",
          hint3: "1 − 0.75 = 0.25",
        },
      ],
    },
  },

  {
    id: "6.2",
    title: "Relative Frequency",
    slug: "relative-frequency",
    pageRef: "Pages 140-141",
    difficulty: "foundation",
    learn: {
      explanation:
        "Relative frequency is an estimate of probability based on experimental results. It is calculated by dividing the number of times an outcome happens by the total number of trials.",
      keyPoints: [
        "Relative frequency = frequency ÷ total trials",
        "It is based on experimental data, not theoretical assumptions",
        "More trials usually give a more reliable estimate",
        "A fair object has outcomes that are roughly equally likely",
        "A biased object has outcomes that are not equally likely",
      ],
      workedExamples: [
        {
          problem:
            "A dice is rolled 100 times. The results are: 1 = 15, 2 = 13, 3 = 4, 4 = 45, 5 = 16, 6 = 7. Does the dice appear biased?",
          image: PAGE_IMAGES.p140_141,
          steps: [
            {
              step: 1,
              instruction: "Calculate relative frequencies.",
              working: "1: 0.15, 2: 0.13, 3: 0.04, 4: 0.45, 5: 0.16, 6: 0.07",
            },
            {
              step: 2,
              instruction: "Compare with a fair dice.",
              working: "For a fair dice, each outcome should be close to 1/6 ≈ 0.167.",
            },
            {
              step: 3,
              instruction: "Look for unusually large differences.",
              working: "The number 4 appears 45 times, much more than expected.",
            },
          ],
          answer: "Yes. The dice appears biased towards 4.",
        },
      ],
      whyItWorks:
        "The more trials you carry out, the closer relative frequency usually gets to the true probability.",
    },
    practice: {
      warmUp: [
        {
          id: "6.2.w1",
          type: "number",
          question:
            "A coin is flipped 20 times and lands heads 11 times. Find the relative frequency of heads.",
          answer: "0.55",
          hint1: "Relative frequency = frequency ÷ total trials.",
          hint2: "11 ÷ 20",
          hint3: "0.55",
        },
      ],
      level1: [
        {
          id: "6.2.1",
          type: "number",
          question:
            "A coin is flipped 50 times and lands heads 28 times. What is the relative frequency of heads?",
          answer: "0.56",
          hint1: "28 ÷ 50",
          hint2: "= 0.56",
          hint3: "0.56",
        },
      ],
      level2: [
        {
          id: "6.2.2",
          type: "choice",
          question:
            "A spinner lands on red 45 times out of 100. The spinner has 4 equal sections. Does it appear biased?",
          options: ["Yes", "No"],
          answer: "Yes",
          hint1: "If fair, each section should appear about 100 ÷ 4 = 25 times.",
          hint2: "45 is much higher than 25.",
          hint3: "Yes",
        },
      ],
      level3: [
        {
          id: "6.2.3",
          type: "number",
          question:
            "A button is tested 240 times and fails 18 times. Estimate the probability that it fails.",
          answer: "0.075",
          hint1: "Use relative frequency.",
          hint2: "18 ÷ 240",
          hint3: "0.075",
        },
      ],
    },
  },

  {
    id: "6.3",
    title: "Sample Spaces",
    slug: "sample-spaces",
    pageRef: "Pages 138-141",
    difficulty: "foundation",
    learn: {
      explanation:
        "A sample space is a list or table of all possible outcomes. It is especially useful when two events happen, such as rolling two dice or choosing from two menus.",
      keyPoints: [
        "A sample space must include every possible outcome",
        "Tables help organise outcomes from two events",
        "Count favourable outcomes carefully",
        "P(event) = favourable outcomes ÷ total outcomes",
        "Outcomes must be equally likely before simple counting probabilities are used",
      ],
      workedExamples: [
        {
          problem:
            "A fair coin is flipped and a fair dice is rolled. Find P(head and an even number).",
          image: PAGE_IMAGES.p140_141,
          steps: [
            {
              step: 1,
              instruction: "Count all outcomes.",
              working: "Coin has 2 outcomes and dice has 6 outcomes, so total = 2 × 6 = 12.",
            },
            {
              step: 2,
              instruction: "Count favourable outcomes.",
              working: "Head and even: H2, H4, H6. There are 3 favourable outcomes.",
            },
            {
              step: 3,
              instruction: "Calculate the probability.",
              working: "3/12 = 1/4",
            },
          ],
          answer: "1/4",
        },
      ],
      whyItWorks:
        "A sample space prevents missed outcomes and makes probability questions easier to count accurately.",
    },
    practice: {
      warmUp: [
        {
          id: "6.3.w1",
          type: "number",
          question:
            "A coin is flipped and a spinner with 5 equal sections is spun. How many total outcomes are there?",
          answer: "10",
          hint1: "Multiply the number of outcomes.",
          hint2: "2 × 5",
          hint3: "10",
        },
      ],
      level1: [
        {
          id: "6.3.1",
          type: "number",
          question:
            "Two fair coins are flipped. How many possible outcomes are there?",
          answer: "4",
          hint1: "Each coin has 2 outcomes.",
          hint2: "2 × 2",
          hint3: "4",
        },
        {
          id: "6.3.2",
          type: "number",
          question:
            "A fair dice is rolled and a fair coin is flipped. How many possible outcomes are there?",
          answer: "12",
          hint1: "Dice has 6 outcomes and coin has 2 outcomes.",
          hint2: "6 × 2",
          hint3: "12",
        },
      ],
      level2: [
        {
          id: "6.3.3",
          type: "number",
          question:
            "Two fair dice are rolled. Find P(total score is 7). Give your answer as a fraction.",
          answer: "1/6",
          hint1: "There are 36 total outcomes.",
          hint2: "Score 7: 1+6, 2+5, 3+4, 4+3, 5+2, 6+1.",
          hint3: "6/36 = 1/6",
        },
      ],
    },
  },

  {
    id: "6.4",
    title: "Venn Diagrams",
    slug: "venn-diagrams",
    pageRef: "Pages 142-147",
    difficulty: "intermediate",
    learn: {
      explanation:
        "Venn diagrams show sets visually. The overlap shows items in both sets. The union shows items in either set or both. The complement shows everything outside a set.",
      keyPoints: [
        "A set is a collection of items, usually written in curly brackets",
        "ξ is the universal set",
        "A ∩ B means A and B: the overlap",
        "A ∪ B means A or B or both: the union",
        "A' means not A: the complement",
        "Probabilities from Venn diagrams use number in region ÷ total",
      ],
      workedExamples: [
        {
          problem:
            "ξ = {1,2,3,4,5,6,7,8,9,10}, P = {factors of 10}, Q = {even numbers}. Find P ∩ Q.",
          image: PAGE_IMAGES.p142_143,
          steps: [
            {
              step: 1,
              instruction: "List the two sets.",
              working: "P = {1, 2, 5, 10}, Q = {2, 4, 6, 8, 10}",
            },
            {
              step: 2,
              instruction: "Find the values in both sets.",
              working: "2 and 10 are in both P and Q.",
            },
          ],
          answer: "{2, 10}",
        },
        {
          problem:
            "In a group of 40 pupils, 22 study French, 18 study Spanish and 7 study both. How many study neither?",
          image: PAGE_IMAGES.p144_145,
          steps: [
            {
              step: 1,
              instruction: "Find French only.",
              working: "22 − 7 = 15",
            },
            {
              step: 2,
              instruction: "Find Spanish only.",
              working: "18 − 7 = 11",
            },
            {
              step: 3,
              instruction: "Find total studying at least one language.",
              working: "15 + 7 + 11 = 33",
            },
            {
              step: 4,
              instruction: "Subtract from the total.",
              working: "40 − 33 = 7",
            },
          ],
          answer: "7 pupils",
        },
      ],
      whyItWorks:
        "Venn diagrams separate groups into clear regions, so each item is counted once and only once.",
    },
    practice: {
      warmUp: [
        {
          id: "6.4.w1",
          type: "choice",
          question: "What does A ∩ B mean?",
          options: ["A or B", "A and B", "Not A", "Everything"],
          answer: "A and B",
          hint1: "The symbol ∩ means intersection.",
          hint2: "The intersection is the overlap.",
          hint3: "A and B",
        },
      ],
      level1: [
        {
          id: "6.4.1",
          type: "number",
          question:
            "ξ = {1 to 20}, P = {square numbers}, Q = {factors of 20}. How many elements are in P ∩ Q?",
          answer: "2",
          hint1: "P = {1, 4, 9, 16}.",
          hint2: "Q = {1, 2, 4, 5, 10, 20}.",
          hint3: "P ∩ Q = {1, 4}, so there are 2 elements.",
        },
      ],
      level2: [
        {
          id: "6.4.2",
          type: "number",
          question:
            "300 Year 9 students: 120 play music, 30 play both music and art, and 100 play neither. How many play art only?",
          answer: "80",
          hint1: "Music only = 120 − 30 = 90.",
          hint2: "At least one = 300 − 100 = 200.",
          hint3: "Art only = 200 − 90 − 30 = 80.",
        },
      ],
      level3: [
        {
          id: "6.4.3",
          type: "number",
          question:
            "In a group of 50, 28 like tea, 24 like coffee and 10 like neither. How many like both?",
          answer: "12",
          hint1: "At least one = 50 − 10 = 40.",
          hint2: "Use T + C − both = union.",
          hint3: "28 + 24 − both = 40, so both = 12.",
        },
      ],
    },
  },

  {
    id: "6.5",
    title: "Data Types & Charts",
    slug: "data-charts",
    pageRef: "Pages 147-151",
    difficulty: "foundation",
    learn: {
      explanation:
        "Data can be qualitative or quantitative. Quantitative data can be discrete or continuous. Different data types suit different diagrams, such as bar charts, pie charts, frequency polygons and histograms.",
      keyPoints: [
        "Qualitative data uses categories or descriptions",
        "Quantitative data uses numbers",
        "Discrete data is counted in separate values",
        "Continuous data can take any value in a range",
        "Bar charts usually have gaps between bars",
        "Pie chart sector angle = category frequency ÷ total × 360",
        "Frequency polygons plot frequencies at class midpoints",
        "Histograms are used for grouped continuous data",
      ],
      workedExamples: [
        {
          problem:
            "Draw a pie chart for sandwich choices: Cheese 6, Ham 15, Egg 20, Tuna 16, Other 3.",
          image: PAGE_IMAGES.p148_149,
          steps: [
            {
              step: 1,
              instruction: "Find the total frequency.",
              working: "6 + 15 + 20 + 16 + 3 = 60",
            },
            {
              step: 2,
              instruction: "Find the angle multiplier.",
              working: "360 ÷ 60 = 6",
            },
            {
              step: 3,
              instruction: "Multiply each frequency by 6.",
              working: "Cheese 36°, Ham 90°, Egg 120°, Tuna 96°, Other 18°",
            },
          ],
          answer: "36°, 90°, 120°, 96°, 18°",
        },
      ],
      whyItWorks:
        "Charts turn numerical or categorical information into visual patterns, making comparisons easier.",
    },
    practice: {
      warmUp: [
        {
          id: "6.5.w1",
          type: "choice",
          question: "Eye colour is an example of:",
          options: ["Qualitative", "Discrete", "Continuous"],
          answer: "Qualitative",
          hint1: "Eye colour is not a number.",
          hint2: "It is a category.",
          hint3: "Qualitative",
        },
      ],
      level1: [
        {
          id: "6.5.1",
          type: "choice",
          question: "Number of spectators at a match is:",
          options: ["Qualitative", "Discrete", "Continuous"],
          answer: "Discrete",
          hint1: "It is numerical data.",
          hint2: "You count whole people.",
          hint3: "Discrete",
        },
        {
          id: "6.5.2",
          type: "choice",
          question: "Weight of an egg is:",
          options: ["Qualitative", "Discrete", "Continuous"],
          answer: "Continuous",
          hint1: "It is numerical data.",
          hint2: "It can be measured to any level of accuracy.",
          hint3: "Continuous",
        },
      ],
      level2: [
        {
          id: "6.5.3",
          type: "number",
          question:
            "A pie chart sector is 60° out of 360°. What fraction of the data does this represent?",
          answer: "1/6",
          hint1: "Write 60/360.",
          hint2: "Simplify the fraction.",
          hint3: "1/6",
        },
        {
          id: "6.5.4",
          type: "number",
          question:
            "A category has frequency 12 out of a total of 48. What angle should its pie chart sector be?",
          answer: "90",
          hint1: "Angle = frequency ÷ total × 360.",
          hint2: "12 ÷ 48 × 360",
          hint3: "90°",
        },
      ],
    },
  },

  {
    id: "6.6",
    title: "Averages: Mean, Median, Mode & Range",
    slug: "averages",
    pageRef: "Pages 152-157",
    difficulty: "foundation",
    learn: {
      explanation:
        "Averages describe the centre of a data set. The mode is the most common value, the median is the middle value, and the mean is the total divided by the number of values. The range measures spread.",
      keyPoints: [
        "Mode = most frequent value",
        "Median = middle value after putting data in order",
        "Mean = total ÷ number of values",
        "Range = highest value − lowest value",
        "Always order the data before finding the median",
        "The mean can be affected by extreme values",
      ],
      workedExamples: [
        {
          problem:
            "Find the mean, median, mode and range of: 6, 4, 7, 1, 2, 6, 3, 5.",
          image: PAGE_IMAGES.p152_153,
          steps: [
            {
              step: 1,
              instruction: "Put the values in order.",
              working: "1, 2, 3, 4, 5, 6, 6, 7",
            },
            {
              step: 2,
              instruction: "Find the mode.",
              working: "6 appears twice, so mode = 6.",
            },
            {
              step: 3,
              instruction: "Find the median.",
              working: "There are 8 values, so median = (4 + 5) ÷ 2 = 4.5.",
            },
            {
              step: 4,
              instruction: "Find the mean.",
              working: "(1 + 2 + 3 + 4 + 5 + 6 + 6 + 7) ÷ 8 = 34 ÷ 8 = 4.25.",
            },
            {
              step: 5,
              instruction: "Find the range.",
              working: "7 − 1 = 6.",
            },
          ],
          answer: "Mode = 6, median = 4.5, mean = 4.25, range = 6",
        },
      ],
      whyItWorks:
        "Different averages reveal different features of a data set, so choosing the right average depends on the question.",
    },
    practice: {
      warmUp: [
        {
          id: "6.6.w1",
          type: "number",
          question: "Find the median of: 1, 4, 5, 8, 12.",
          answer: "5",
          hint1: "The data is already in order.",
          hint2: "There are 5 values, so the middle is the 3rd.",
          hint3: "5",
        },
        {
          id: "6.6.w2",
          type: "number",
          question: "Find the range of: 2, 3, 5, 7, 11.",
          answer: "9",
          hint1: "Range = highest − lowest.",
          hint2: "11 − 2",
          hint3: "9",
        },
      ],
      level1: [
        {
          id: "6.6.1",
          type: "number",
          question: "Find the mean of: 3, 7, 5, 9, 6.",
          answer: "6",
          hint1: "Add the values.",
          hint2: "3 + 7 + 5 + 9 + 6 = 30.",
          hint3: "30 ÷ 5 = 6.",
        },
        {
          id: "6.6.2",
          type: "number",
          question: "Find the mode of: 2, 3, 5, 2, 7, 2, 4.",
          answer: "2",
          hint1: "Which value appears most often?",
          hint2: "2 appears 3 times.",
          hint3: "2",
        },
      ],
      level2: [
        {
          id: "6.6.3",
          type: "number",
          question:
            "The mean of 3 quiz scores is 45. The first two scores are 42 and 49. Find the third score.",
          answer: "44",
          hint1: "Total = mean × number of scores.",
          hint2: "45 × 3 = 135 and 42 + 49 = 91.",
          hint3: "135 − 91 = 44.",
        },
        {
          id: "6.6.4",
          type: "number",
          question:
            "The 4th number is missing. The numbers are 5, 7, ?, 8. The mean is 8. Find the missing number.",
          answer: "12",
          hint1: "Total = 8 × 4 = 32.",
          hint2: "Known total = 5 + 7 + 8 = 20.",
          hint3: "32 − 20 = 12.",
        },
      ],
    },
  },

  {
    id: "6.7",
    title: "Frequency Tables & Grouped Data",
    slug: "frequency-tables-grouped-data",
    pageRef: "Pages 152-157",
    difficulty: "intermediate",
    learn: {
      explanation:
        "Frequency tables show how often values occur. For grouped data, the exact values are unknown, so the mean must be estimated using class midpoints.",
      keyPoints: [
        "For frequency tables, mean = Σ(f × x) ÷ Σf",
        "Σ means sum of",
        "For grouped data, use the midpoint of each class interval",
        "Estimated mean = Σ(f × midpoint) ÷ Σf",
        "The modal class is the class interval with the highest frequency",
        "Grouped data gives estimates, not exact values",
      ],
      workedExamples: [
        {
          problem:
            "A frequency table shows scores: 1 has frequency 2, 2 has frequency 5, 3 has frequency 4, 4 has frequency 1. Find the mean score.",
          image: PAGE_IMAGES.p152_153,
          steps: [
            {
              step: 1,
              instruction: "Multiply each value by its frequency.",
              working: "1×2 = 2, 2×5 = 10, 3×4 = 12, 4×1 = 4",
            },
            {
              step: 2,
              instruction: "Add the products.",
              working: "2 + 10 + 12 + 4 = 28",
            },
            {
              step: 3,
              instruction: "Add the frequencies.",
              working: "2 + 5 + 4 + 1 = 12",
            },
            {
              step: 4,
              instruction: "Divide.",
              working: "Mean = 28 ÷ 12 = 2.333...",
            },
          ],
          answer: "2.33 to 2 decimal places",
        },
        {
          problem:
            "Estimate the mean from grouped data: 0≤x<10 frequency 3, 10≤x<20 frequency 7, 20≤x<30 frequency 10.",
          image: PAGE_IMAGES.p156_157,
          steps: [
            {
              step: 1,
              instruction: "Find midpoints.",
              working: "5, 15, 25",
            },
            {
              step: 2,
              instruction: "Multiply midpoints by frequencies.",
              working: "5×3 = 15, 15×7 = 105, 25×10 = 250",
            },
            {
              step: 3,
              instruction: "Add and divide by total frequency.",
              working: "(15 + 105 + 250) ÷ 20 = 370 ÷ 20 = 18.5",
            },
          ],
          answer: "18.5",
        },
      ],
      whyItWorks:
        "A frequency table compresses repeated data. Multiplying each value by its frequency reconstructs the total efficiently.",
    },
    practice: {
      warmUp: [
        {
          id: "6.7.w1",
          type: "number",
          question:
            "In a frequency table, the value 6 has frequency 4. What is f × x for this row?",
          answer: "24",
          hint1: "Multiply the value by its frequency.",
          hint2: "6 × 4",
          hint3: "24",
        },
      ],
      level1: [
        {
          id: "6.7.1",
          type: "number",
          question:
            "Scores: 2 has frequency 3, 4 has frequency 2. Find the mean.",
          answer: "2.8",
          hint1: "Total score = 2×3 + 4×2.",
          hint2: "Total score = 14, total frequency = 5.",
          hint3: "14 ÷ 5 = 2.8.",
        },
      ],
      level2: [
        {
          id: "6.7.2",
          type: "number",
          question:
            "Grouped data: 0-10 frequency 4, 10-20 frequency 6. Estimate the mean.",
          answer: "11",
          hint1: "Use midpoints 5 and 15.",
          hint2: "5×4 + 15×6 = 20 + 90 = 110.",
          hint3: "110 ÷ 10 = 11.",
        },
      ],
      level3: [
        {
          id: "6.7.3",
          type: "number",
          question:
            "Grouped data: 0-20 frequency 5, 20-40 frequency 7, 40-60 frequency 8. Estimate the mean.",
          answer: "33",
          hint1: "Use midpoints 10, 30 and 50.",
          hint2: "10×5 + 30×7 + 50×8 = 660.",
          hint3: "660 ÷ 20 = 33.",
        },
      ],
    },
  },

  {
    id: "6.8",
    title: "Scatter Graphs",
    slug: "scatter-graphs",
    pageRef: "Pages 155-157",
    difficulty: "intermediate",
    learn: {
      explanation:
        "Scatter graphs show the relationship between two variables. The pattern of points shows correlation. A line of best fit can be used to estimate values.",
      keyPoints: [
        "Positive correlation: as one variable increases, the other increases",
        "Negative correlation: as one variable increases, the other decreases",
        "No correlation: no clear relationship",
        "Strong correlation has points close to a line",
        "Weak correlation has points more spread out",
        "A line of best fit should go through the middle of the points",
        "Interpolation predicts within the data range",
        "Extrapolation predicts outside the data range and is less reliable",
      ],
      workedExamples: [
        {
          problem:
            "Car value and car age have a downward pattern on a scatter graph. What type of correlation is this?",
          image: PAGE_IMAGES.p154_155,
          steps: [
            {
              step: 1,
              instruction: "Describe the relationship.",
              working: "As car age increases, car value decreases.",
            },
            {
              step: 2,
              instruction: "Name the correlation.",
              working: "One variable goes up while the other goes down, so the correlation is negative.",
            },
          ],
          answer: "Negative correlation",
        },
      ],
      whyItWorks:
        "Scatter graphs reveal relationships between paired data values that may not be obvious from a table.",
    },
    practice: {
      warmUp: [
        {
          id: "6.8.w1",
          type: "choice",
          question:
            "As revision time increases, test score tends to increase. What correlation is this?",
          options: ["Positive", "Negative", "None"],
          answer: "Positive",
          hint1: "Both variables increase together.",
          hint2: "This is an upward trend.",
          hint3: "Positive",
        },
      ],
      level1: [
        {
          id: "6.8.1",
          type: "choice",
          question: "Temperature and ice cream sales usually have what correlation?",
          options: ["Positive", "Negative", "None"],
          answer: "Positive",
          hint1: "Hotter weather usually means more ice cream sales.",
          hint2: "Both increase together.",
          hint3: "Positive",
        },
        {
          id: "6.8.2",
          type: "choice",
          question: "Age of car and value usually have what correlation?",
          options: ["Positive", "Negative", "None"],
          answer: "Negative",
          hint1: "Older cars are usually worth less.",
          hint2: "As age goes up, value goes down.",
          hint3: "Negative",
        },
      ],
      level2: [
        {
          id: "6.8.3",
          type: "choice",
          question: "Shoe size and IQ would usually have what correlation?",
          options: ["Positive", "Negative", "None"],
          answer: "None",
          hint1: "There is no sensible relationship.",
          hint2: "The points would be randomly scattered.",
          hint3: "No correlation",
        },
      ],
      level3: [
        {
          id: "6.8.4",
          type: "choice",
          question:
            "Using a line of best fit to predict outside the range of the data is called:",
          options: ["Interpolation", "Extrapolation", "Correlation", "Sampling"],
          answer: "Extrapolation",
          hint1: "Outside the data range is more risky.",
          hint2: "Inter means within; extra means outside.",
          hint3: "Extrapolation",
        },
      ],
    },
  },

  {
    id: "6.9",
    title: "Tree Diagrams",
    slug: "tree-diagrams",
    pageRef: "GCSE expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "A probability tree diagram shows possible outcomes as branches. Multiply along branches for a path. Add separate successful paths when there is more than one way for an event to happen.",
      keyPoints: [
        "Each branch shows one outcome and its probability",
        "Branches from the same point must add to 1",
        "Along a path means multiply",
        "Across separate paths means add",
        "With replacement means probabilities stay the same",
        "Without replacement means probabilities change",
        "At least one problems are often easiest using 1 − P(none)",
      ],
      workedExamples: [
        {
          problem: "A coin is flipped twice. Find P(both heads).",
          steps: [
            {
              step: 1,
              instruction: "Find the probability of heads on each flip.",
              working: "P(H) = 1/2",
            },
            {
              step: 2,
              instruction: "Multiply along the HH path.",
              working: "1/2 × 1/2 = 1/4",
            },
          ],
          answer: "1/4",
        },
        {
          problem:
            "A bag contains 5 red counters and 3 blue counters. Two counters are drawn without replacement. Find P(both red).",
          steps: [
            {
              step: 1,
              instruction: "Find the probability of red on the first draw.",
              working: "5/8",
            },
            {
              step: 2,
              instruction: "Find the probability of red on the second draw after one red is removed.",
              working: "4/7",
            },
            {
              step: 3,
              instruction: "Multiply along the path.",
              working: "5/8 × 4/7 = 20/56 = 5/14",
            },
          ],
          answer: "5/14",
        },
      ],
      whyItWorks:
        "Each complete path represents one possible sequence of outcomes. Multiplying gives the probability of that sequence.",
    },
    practice: {
      warmUp: [
        {
          id: "6.9.w1",
          type: "number",
          question: "Two dice are rolled. Find P(both are 6). Give a fraction.",
          answer: "1/36",
          hint1: "P(6) = 1/6 for each dice.",
          hint2: "1/6 × 1/6",
          hint3: "1/36",
        },
        {
          id: "6.9.w2",
          type: "number",
          question: "A fair coin is flipped twice. Find P(2 tails).",
          answer: "0.25",
          hint1: "P(T) = 0.5 each time.",
          hint2: "0.5 × 0.5",
          hint3: "0.25",
        },
      ],
      level1: [
        {
          id: "6.9.1",
          type: "number",
          question:
            "A bag has 4 red and 6 blue counters. A counter is chosen, replaced, then another is chosen. Find P(red then blue).",
          answer: "0.24",
          hint1: "With replacement means probabilities stay the same.",
          hint2: "P(R) = 0.4 and P(B) = 0.6.",
          hint3: "0.4 × 0.6 = 0.24.",
        },
        {
          id: "6.9.2",
          type: "number",
          question: "A coin is flipped 3 times. Find P(all heads). Give a fraction.",
          answer: "1/8",
          hint1: "Multiply 1/2 three times.",
          hint2: "1/2 × 1/2 × 1/2",
          hint3: "1/8",
        },
      ],
      level2: [
        {
          id: "6.9.3",
          type: "number",
          question:
            "A bag has 7 red and 3 blue counters. Two counters are drawn without replacement. Find P(both blue) to 3 d.p.",
          answer: "0.067",
          hint1: "P(blue first) = 3/10.",
          hint2: "P(blue second | blue first) = 2/9.",
          hint3: "3/10 × 2/9 = 6/90 = 0.067.",
        },
      ],
      level3: [
        {
          id: "6.9.4",
          type: "number",
          question:
            "P(A) = 0.6, P(B|A) = 0.5 and P(B|not A) = 0.3. Find P(B).",
          answer: "0.42",
          hint1: "P(B) = P(A and B) + P(not A and B).",
          hint2: "0.6 × 0.5 + 0.4 × 0.3",
          hint3: "0.30 + 0.12 = 0.42.",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A factory has two machines. Machine A makes 70% of the items and machine B makes 30%. Machine A has a defect rate of 2%; machine B has a defect rate of 5%. A random item is found to be defective. Use a tree diagram to find the probability that it came from machine B. Give your answer to 3 decimal places.",
      successCriteria: [
        "Sets up P(A) = 0.7 and P(B) = 0.3",
        "Uses P(defect | A) = 0.02 and P(defect | B) = 0.05",
        "Calculates P(B and defect) = 0.3 × 0.05 = 0.015",
        "Calculates P(defect) = 0.7 × 0.02 + 0.3 × 0.05 = 0.029",
        "Calculates P(B | defect) = 0.015 ÷ 0.029 ≈ 0.517",
      ],
    },
  },

  {
    id: "6.10",
    title: "Conditional Probability",
    slug: "conditional-probability",
    pageRef: "GCSE expansion — Higher",
    difficulty: "higher",
    learn: {
      explanation:
        "Conditional probability is the probability of one event happening given that another event has already happened. It is written P(A|B), meaning probability of A given B.",
      keyPoints: [
        "P(A|B) means probability of A given B",
        "P(A|B) = P(A and B) ÷ P(B)",
        "Without replacement questions are conditional because the second probability changes",
        "Venn diagrams and two-way tables are useful for conditional probability",
        "Independent events satisfy P(A|B) = P(A)",
      ],
      workedExamples: [
        {
          problem:
            "In a class of 30 pupils, 18 like Maths, 12 like Science and 6 like both. A pupil who likes Maths is chosen. What is the probability they also like Science?",
          steps: [
            {
              step: 1,
              instruction: "Restrict the sample space to pupils who like Maths.",
              working: "There are 18 pupils who like Maths.",
            },
            {
              step: 2,
              instruction: "Count how many of those also like Science.",
              working: "6 pupils like both.",
            },
            {
              step: 3,
              instruction: "Write the probability.",
              working: "6/18 = 1/3",
            },
          ],
          answer: "1/3",
        },
        {
          problem:
            "A bag contains 4 red and 6 blue counters. Two counters are drawn without replacement. Find P(second is red | first was blue).",
          steps: [
            {
              step: 1,
              instruction: "Update the bag after the first blue is removed.",
              working: "There are now 4 red and 5 blue counters, 9 total.",
            },
            {
              step: 2,
              instruction: "Find the probability of red now.",
              working: "4/9",
            },
          ],
          answer: "4/9",
        },
      ],
      whyItWorks:
        "Conditioning changes the sample space. Instead of considering all outcomes, you only consider the outcomes where the given event has happened.",
    },
    practice: {
      warmUp: [
        {
          id: "6.10.w1",
          type: "number",
          question: "P(A and B) = 0.2 and P(B) = 0.5. Find P(A|B).",
          answer: "0.4",
          hint1: "Use P(A|B) = P(A and B) ÷ P(B).",
          hint2: "0.2 ÷ 0.5",
          hint3: "0.4",
        },
        {
          id: "6.10.w2",
          type: "number",
          question:
            "A bag has 3 red and 7 blue counters. Without replacement, after taking 1 red, find P(red next). Give a fraction.",
          answer: "2/9",
          hint1: "There are now 2 red and 7 blue counters.",
          hint2: "There are 9 counters left.",
          hint3: "2/9",
        },
      ],
      level1: [
        {
          id: "6.10.1",
          type: "number",
          question: "P(A) = 0.6 and P(A and B) = 0.3. Find P(B|A).",
          answer: "0.5",
          hint1: "P(B|A) = P(A and B) ÷ P(A).",
          hint2: "0.3 ÷ 0.6",
          hint3: "0.5",
        },
      ],
      level2: [
        {
          id: "6.10.2",
          type: "number",
          question:
            "In a class of 25, 15 like art, 10 like music and 4 like both. Find P(music | art). Give a fraction.",
          answer: "4/15",
          hint1: "Restrict to the 15 pupils who like art.",
          hint2: "Of those, 4 also like music.",
          hint3: "4/15",
        },
        {
          id: "6.10.3",
          type: "number",
          question:
            "P(X) = 0.4, P(Y) = 0.5 and P(X and Y) = 0.2. Find P(X|Y).",
          answer: "0.4",
          hint1: "P(X|Y) = P(X and Y) ÷ P(Y).",
          hint2: "0.2 ÷ 0.5",
          hint3: "0.4",
        },
      ],
      level3: [
        {
          id: "6.10.4",
          type: "number",
          question:
            "A bag contains 6 red and 4 blue counters. Two counters are drawn without replacement. Find P(both red). Give a fraction.",
          answer: "1/3",
          hint1: "Use 6/10 × 5/9.",
          hint2: "30/90",
          hint3: "1/3",
        },
        {
          id: "6.10.5",
          type: "number",
          question:
            "Using the same bag, find P(second is red | first was blue). Give a fraction.",
          answer: "2/3",
          hint1: "After 1 blue is removed, there are 6 red and 3 blue counters.",
          hint2: "There are 9 counters left.",
          hint3: "6/9 = 2/3",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A medical test is 99% accurate. It correctly identifies disease in 99% of people who have the disease and gives a correct negative result in 99% of healthy people. The disease affects 1 in 1000 people. If a randomly selected person tests positive, what is the probability they actually have the disease? Comment on the result.",
      successCriteria: [
        "Identifies P(disease) = 0.001 and P(no disease) = 0.999",
        "Uses P(positive | disease) = 0.99",
        "Uses P(positive | no disease) = 0.01",
        "Calculates P(positive) = 0.001 × 0.99 + 0.999 × 0.01 ≈ 0.01098",
        "Calculates P(disease | positive) = 0.00099 ÷ 0.01098 ≈ 0.09",
        "Explains that the low base rate makes the result counter-intuitive",
      ],
    },
  },

  {
    id: "6.11",
    title: "Independent & Dependent Events",
    slug: "independent-dependent-events",
    pageRef: "GCSE expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "Events are independent if one event happening does not change the probability of the other. Events are dependent if one event affects the probability of the other.",
      keyPoints: [
        "Independent events: P(A and B) = P(A) × P(B)",
        "Dependent events: P(A and B) = P(A) × P(B|A)",
        "Coin flips and dice rolls are usually independent",
        "Drawing without replacement is dependent",
        "With replacement usually makes draws independent",
        "If P(A and B) = P(A) × P(B), the events are independent",
      ],
      workedExamples: [
        {
          problem: "A coin is flipped and a die is rolled. Find P(head and 6).",
          steps: [
            {
              step: 1,
              instruction: "Decide whether the events are independent.",
              working: "The coin and die do not affect each other, so they are independent.",
            },
            {
              step: 2,
              instruction: "Multiply the probabilities.",
              working: "1/2 × 1/6 = 1/12",
            },
          ],
          answer: "1/12",
        },
        {
          problem:
            "A bag contains 4 red and 6 blue counters. Two counters are drawn without replacement. Find P(both red).",
          steps: [
            {
              step: 1,
              instruction: "Decide whether the events are independent.",
              working: "They are dependent because the first counter is not replaced.",
            },
            {
              step: 2,
              instruction: "Find the first probability.",
              working: "P(red first) = 4/10",
            },
            {
              step: 3,
              instruction: "Find the second probability after one red is removed.",
              working: "P(red second | red first) = 3/9",
            },
            {
              step: 4,
              instruction: "Multiply.",
              working: "4/10 × 3/9 = 12/90 = 2/15",
            },
          ],
          answer: "2/15",
        },
      ],
      whyItWorks:
        "Independent events keep the same probability after another event happens. Dependent events require the probability to be updated.",
    },
    practice: {
      warmUp: [
        {
          id: "6.11.w1",
          type: "choice",
          question: "Two coin flips are:",
          options: ["Independent", "Dependent"],
          answer: "Independent",
          hint1: "Does the first flip affect the second?",
          hint2: "No.",
          hint3: "Independent",
        },
        {
          id: "6.11.w2",
          type: "choice",
          question: "Two cards drawn without replacement are:",
          options: ["Independent", "Dependent"],
          answer: "Dependent",
          hint1: "Does the first draw change the deck?",
          hint2: "Yes, there is one fewer card.",
          hint3: "Dependent",
        },
      ],
      level1: [
        {
          id: "6.11.1",
          type: "number",
          question:
            "P(rain Monday) = 0.4 and P(rain Tuesday) = 0.4. The events are independent. Find P(rain both days).",
          answer: "0.16",
          hint1: "Independent means multiply.",
          hint2: "0.4 × 0.4",
          hint3: "0.16",
        },
        {
          id: "6.11.2",
          type: "choice",
          question:
            "Drawing a card from a deck with replacement means the second draw is:",
          options: ["Independent of the first", "Dependent on the first"],
          answer: "Independent of the first",
          hint1: "With replacement restores the deck.",
          hint2: "The probabilities are unchanged.",
          hint3: "Independent of the first",
        },
      ],
      level2: [
        {
          id: "6.11.3",
          type: "number",
          question:
            "A bag contains 5 red and 3 blue counters. Two counters are drawn without replacement. Find P(red then blue). Give a fraction.",
          answer: "15/56",
          hint1: "P(red first) = 5/8.",
          hint2: "After red is removed, P(blue second) = 3/7.",
          hint3: "5/8 × 3/7 = 15/56.",
        },
        {
          id: "6.11.4",
          type: "number",
          question:
            "P(A) = 0.6 and P(B) = 0.5. If A and B are independent, find P(A and B).",
          answer: "0.3",
          hint1: "Multiply the probabilities.",
          hint2: "0.6 × 0.5",
          hint3: "0.3",
        },
      ],
      level3: [
        {
          id: "6.11.5",
          type: "choice",
          question:
            "P(A and B) = 0.2, P(A) = 0.4 and P(B) = 0.5. Are A and B independent?",
          options: ["Yes", "No"],
          answer: "Yes",
          hint1: "Test whether P(A) × P(B) = P(A and B).",
          hint2: "0.4 × 0.5 = 0.2.",
          hint3: "Yes",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "A teacher claims: 'When I roll a die twice, getting a 6 the first time makes me more likely to roll a 6 the second time.' Explain whether this is correct. Then explain how the answer changes if the teacher draws balls from a bag of 10 balls, one labelled 6, without replacement.",
      successCriteria: [
        "Identifies dice rolls as independent",
        "States that the second dice roll still has probability 1/6",
        "Explains that the first dice roll has no physical effect on the second",
        "Identifies the bag example as dependent",
        "Explains that without replacement changes the sample space",
        "States that if the first ball was the 6, P(6 second) = 0",
        "States that if the first ball was not the 6, P(6 second) = 1/9",
      ],
    },
  },

  {
    id: "6.12",
    title: "Cumulative Frequency & Box Plots",
    slug: "cumulative-frequency-box-plots",
    pageRef: "GCSE expansion — Higher",
    difficulty: "higher",
    learn: {
      explanation:
        "Cumulative frequency shows the running total of frequencies. It is used to estimate the median, quartiles and interquartile range. Box plots summarise the distribution using five key values.",
      keyPoints: [
        "Cumulative frequency means running total",
        "The median is at n/2",
        "The lower quartile is at n/4",
        "The upper quartile is at 3n/4",
        "Interquartile range = upper quartile − lower quartile",
        "A box plot shows minimum, lower quartile, median, upper quartile and maximum",
        "Box plots are useful for comparing spread and central tendency",
      ],
      workedExamples: [
        {
          problem:
            "A data set has 80 values. On a cumulative frequency graph, where would you read the median, lower quartile and upper quartile?",
          steps: [
            {
              step: 1,
              instruction: "Find the median position.",
              working: "n/2 = 80/2 = 40",
            },
            {
              step: 2,
              instruction: "Find the lower quartile position.",
              working: "n/4 = 80/4 = 20",
            },
            {
              step: 3,
              instruction: "Find the upper quartile position.",
              working: "3n/4 = 3×80/4 = 60",
            },
          ],
          answer: "Lower quartile at 20, median at 40, upper quartile at 60",
        },
        {
          problem:
            "A box plot has lower quartile 18 and upper quartile 31. Find the interquartile range.",
          steps: [
            {
              step: 1,
              instruction: "Use IQR = upper quartile − lower quartile.",
              working: "31 − 18 = 13",
            },
          ],
          answer: "13",
        },
      ],
      whyItWorks:
        "Cumulative frequency converts grouped data into a running total, allowing key positions in the distribution to be estimated.",
    },
    practice: {
      warmUp: [
        {
          id: "6.12.w1",
          type: "number",
          question:
            "A data set has 60 values. What position gives the median on a cumulative frequency graph?",
          answer: "30",
          hint1: "Median position = n/2.",
          hint2: "60 ÷ 2",
          hint3: "30",
        },
      ],
      level1: [
        {
          id: "6.12.1",
          type: "number",
          question:
            "A data set has 100 values. What position gives the lower quartile?",
          answer: "25",
          hint1: "Lower quartile position = n/4.",
          hint2: "100 ÷ 4",
          hint3: "25",
        },
        {
          id: "6.12.2",
          type: "number",
          question:
            "A data set has 100 values. What position gives the upper quartile?",
          answer: "75",
          hint1: "Upper quartile position = 3n/4.",
          hint2: "3 × 100 ÷ 4",
          hint3: "75",
        },
      ],
      level2: [
        {
          id: "6.12.3",
          type: "number",
          question:
            "A box plot has lower quartile 12 and upper quartile 29. Find the interquartile range.",
          answer: "17",
          hint1: "IQR = upper quartile − lower quartile.",
          hint2: "29 − 12",
          hint3: "17",
        },
      ],
      level3: [
        {
          id: "6.12.4",
          type: "number",
          question:
            "A box plot has minimum 5, lower quartile 14, median 21, upper quartile 34 and maximum 50. Find the range.",
          answer: "45",
          hint1: "Range = maximum − minimum.",
          hint2: "50 − 5",
          hint3: "45",
        },
      ],
    },
    aiChallenge: {
      prompt:
        "Two classes sit the same test. Class A has median 62 and interquartile range 12. Class B has median 58 and interquartile range 25. Compare the performance of the two classes.",
      successCriteria: [
        "States that Class A has the higher median",
        "Explains that Class A generally performed better on average",
        "States that Class A has the smaller interquartile range",
        "Explains that Class A's results were more consistent",
        "States that Class B had more spread or variability",
      ],
    },
  },
];

export default probabilityTopics;